import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Phone, MonitorUp, MonitorX, Maximize2, Minimize2, Signal } from 'lucide-react';
import { useChat } from '../../chat/hooks/useChat';
import { startTone } from './ringtone';
import { formatDuration } from '../../chat/calls/callLogic';

/** Attach a MediaStream to a media element and keep it playing. */
function useStream(elRef, stream) {
    useEffect(() => {
        const el = elRef.current;
        if (!el) return;
        if (el.srcObject !== stream) el.srcObject = stream || null;
        if (stream) el.play?.().catch(() => { /* autoplay blocked: the element retries when the user clicks */ });
    }, [elRef, stream]);
}

const QUALITY = {
    good: { bars: 3, label: 'Good connection', cls: 'text-emerald-400' },
    fair: { bars: 2, label: 'Fair connection', cls: 'text-amber-400' },
    poor: { bars: 1, label: 'Poor connection — video may drop', cls: 'text-red-400' },
    unknown: { bars: 0, label: 'Measuring connection…', cls: 'text-slate-400' },
};

const STATUS_TEXT = {
    preparing: 'Starting…', calling: 'Ringing…', connecting: 'Connecting…',
    reconnecting: 'Reconnecting…', ringing: 'Incoming call',
};

const Avatar = ({ name, photo, size = 'w-28 h-28' }) => (
    <img
        src={photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=4f46e5&color=fff&size=256`}
        alt={name || ''}
        className={`${size} rounded-full object-cover border-4 border-white/10 shadow-2xl`}
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=4f46e5&color=fff`; }}
    />
);

const CtrlButton = ({ onClick, active, danger, label, children, disabled }) => (
    <button
        type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} aria-pressed={active}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-40
            ${danger ? 'bg-red-500 hover:bg-red-600 text-white' : active ? 'bg-white text-slate-900' : 'bg-white/10 hover:bg-white/20 text-white'}`}
    >
        {children}
    </button>
);

/**
 * The one call screen, mounted app-wide (ProtectedRoutes). Everything it shows comes from
 * the call controller via the chat context.
 */
export default function CallModal() {
    const { callControls: c } = useChat();
    const call = c?.call;
    const remoteAudioRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const localVideoRef = useRef(null);
    const [elapsed, setElapsed] = useState(0);
    const [full, setFull] = useState(false);

    // The other person's VOICE always plays through this element, whatever is on screen.
    // The old screen played audio through the <video> tag only, which is unmounted in a
    // voice-call layout — no element, no sound.
    useStream(remoteAudioRef, c?.remoteStream || null);
    useStream(remoteVideoRef, c?.remoteVideoLive ? c.remoteStream : null);
    useStream(localVideoRef, c?.localPreview || null);
    useEffect(() => {
        // A track unmuting does not change the stream object; nudge playback anyway.
        remoteVideoRef.current?.play?.().catch(() => {});
    }, [c?.remoteTick]);

    // Ringtone while ringing; ringback while the other side rings.
    useEffect(() => {
        if (call?.status === 'ringing') return startTone('incoming');
        if (call?.status === 'calling') return startTone('outgoing');
        return undefined;
    }, [call?.status]);

    useEffect(() => {
        if (!call?.connectedAt) { setElapsed(0); return undefined; }
        const tick = () => setElapsed(Math.round((Date.now() - call.connectedAt) / 1000));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [call?.connectedAt]);

    useEffect(() => {
        if (!call) return undefined;
        const onKey = (e) => {
            if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
            if (call.status === 'ringing') return;
            if (e.key === 'm' && e.ctrlKey) { e.preventDefault(); c.toggleMute(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [call, c]);

    if (!call) return null;

    const remoteName = call.otherName || 'Colleague';
    const q = QUALITY[c.quality?.level] || QUALITY.unknown;
    const connected = call.status === 'connected' || call.status === 'reconnecting';
    const canShare = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

    // ---- Incoming ring ----------------------------------------------------------------------
    if (call.status === 'ringing') {
        return (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4" role="alertdialog" aria-label={`Incoming call from ${remoteName}`}>
                <audio ref={remoteAudioRef} autoPlay className="hidden" />
                <div className="bg-slate-900 rounded-3xl p-8 w-full max-w-sm flex flex-col items-center shadow-2xl border border-white/10">
                    <div className="relative mb-5">
                        <span className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping" />
                        <Avatar name={remoteName} photo={call.otherPhoto} />
                    </div>
                    <h3 className="text-xl font-bold text-white text-center">{remoteName}</h3>
                    <p className="text-indigo-300 text-sm mt-1 mb-8">Incoming {call.type === 'video' ? 'video' : 'voice'} call</p>
                    <div className="flex gap-10">
                        <div className="flex flex-col items-center gap-2">
                            <CtrlButton danger onClick={c.endCall} label="Decline"><PhoneOff size={22} /></CtrlButton>
                            <span className="text-xs text-slate-400">Decline</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <button type="button" onClick={c.answerCall} autoFocus aria-label="Accept"
                                className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
                                {call.type === 'video' ? <Video size={22} /> : <Phone size={22} />}
                            </button>
                            <span className="text-xs text-slate-400">Accept</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Active call ------------------------------------------------------------------------
    const sharingRemote = c.remoteMedia?.screen === true;
    return (
        <div className={`fixed z-[10000] ${full ? 'inset-0' : 'inset-0 sm:inset-6'} flex items-center justify-center bg-slate-950/85 backdrop-blur-sm`} role="dialog" aria-label={`Call with ${remoteName}`}>
            <audio ref={remoteAudioRef} autoPlay className="hidden" />
            <div className={`relative w-full h-full ${full ? '' : 'sm:rounded-3xl'} overflow-hidden bg-slate-900 border border-white/5 flex flex-col`}>
                {/* Top bar */}
                <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-b from-black/60 to-transparent">
                    <div className="min-w-0">
                        <div className="text-white font-semibold truncate">{remoteName}</div>
                        <div className="text-xs text-slate-300 flex items-center gap-2">
                            {connected ? formatDuration(elapsed) : STATUS_TEXT[call.status] || call.status}
                            {call.status === 'reconnecting' && <span className="text-amber-300">· network dropped, trying to reconnect</span>}
                            {sharingRemote && <span className="text-indigo-300">· sharing their screen</span>}
                            {c.remoteMedia?.mic === false && <span className="flex items-center gap-1 text-slate-400"><MicOff size={12} /> muted</span>}
                        </div>
                    </div>
                    {connected && (
                        <div className={`flex items-center gap-1.5 text-xs ${q.cls}`} title={`${q.label}${c.quality?.rttMs != null ? ` · ${c.quality.rttMs} ms` : ''}${c.quality?.relayed ? ' · relayed' : ''}`}>
                            <Signal size={16} aria-hidden="true" />
                            <span className="hidden sm:inline">{q.label}</span>
                        </div>
                    )}
                </div>

                {/* Remote */}
                <div className="flex-1 relative flex items-center justify-center bg-black">
                    {c.remoteVideoLive ? (
                        <video ref={remoteVideoRef} autoPlay playsInline
                            // A shared screen must be shown whole, never cropped.
                            className={`w-full h-full ${sharingRemote ? 'object-contain' : 'object-cover'}`} />
                    ) : (
                        <div className="flex flex-col items-center gap-4">
                            <Avatar name={remoteName} photo={call.otherPhoto} size="w-32 h-32" />
                            <div className="text-slate-300 text-sm">
                                {connected ? (call.type === 'video' && c.remoteMedia?.cam === false ? `${remoteName} turned their camera off` : 'Voice call') : STATUS_TEXT[call.status]}
                            </div>
                        </div>
                    )}

                    {/* Me */}
                    {c.localPreview && (
                        <div className="absolute bottom-24 right-4 w-40 sm:w-56 aspect-video rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-slate-800">
                            <video ref={localVideoRef} autoPlay playsInline muted
                                // Mirror the camera like a mirror; NEVER mirror a shared screen —
                                // that is what made shared text read backwards.
                                className={`w-full h-full ${c.previewIsScreen ? 'object-contain' : 'object-cover -scale-x-100'}`} />
                            <span className="absolute bottom-1.5 left-2 text-[10px] font-semibold text-white bg-black/50 rounded px-1.5">
                                {c.previewIsScreen ? 'Your screen' : 'You'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className="absolute bottom-0 inset-x-0 z-20 flex justify-center pb-5 pt-10 bg-gradient-to-t from-black/70 to-transparent">
                    <div className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-slate-900/80 border border-white/10 backdrop-blur">
                        <CtrlButton onClick={c.toggleMute} active={c.muted} label={c.muted ? 'Unmute (Ctrl+M)' : 'Mute (Ctrl+M)'}>
                            {c.muted ? <MicOff size={20} /> : <Mic size={20} />}
                        </CtrlButton>
                        <CtrlButton onClick={c.toggleVideo} active={!c.cameraOn} disabled={!connected && call.status !== 'connecting'}
                            label={c.cameraOn ? 'Turn camera off' : 'Turn camera on'}>
                            {c.cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
                        </CtrlButton>
                        {canShare && (
                            <CtrlButton onClick={c.shareScreen} active={c.sharing} disabled={!connected}
                                label={c.sharing ? 'Stop sharing your screen' : 'Share your screen'}>
                                {c.sharing ? <MonitorX size={20} /> : <MonitorUp size={20} />}
                            </CtrlButton>
                        )}
                        <CtrlButton onClick={() => setFull(f => !f)} label={full ? 'Exit full screen' : 'Full screen'}>
                            {full ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                        </CtrlButton>
                        <CtrlButton danger onClick={c.endCall} label={call.status === 'calling' ? 'Cancel call' : 'End call'}>
                            <PhoneOff size={20} />
                        </CtrlButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
